import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminApi } from '@/api/client';
import DataTable from '@/components/common/DataTable';
import Modal from '@/components/common/Modal';
import ConfirmDialog from '@/components/common/ConfirmDialog';

interface Supplier {
  id: string;
  name: string;
  code: string;
  rating: number;
  isVerified: boolean;
  description: string | null;
  _count: { products: number };
  createdAt: string;
}

export default function SuppliersPage() {
  const { t } = useTranslation();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    rating: 0,
    isVerified: false,
    description: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await adminApi.getSuppliers();
      setSuppliers(response.data);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateModal = () => {
    setSelectedSupplier(null);
    setFormData({ name: '', code: '', rating: 0, isVerified: false, description: '' });
    setError('');
    setIsModalOpen(true);
  };

  const openEditModal = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setFormData({
      name: supplier.name,
      code: supplier.code,
      rating: supplier.rating,
      isVerified: supplier.isVerified,
      description: supplier.description || '',
    });
    setError('');
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');

    try {
      if (selectedSupplier) {
        await adminApi.updateSupplier(selectedSupplier.id, formData);
      } else {
        await adminApi.createSupplier(formData);
      }
      setIsModalOpen(false);
      fetchSuppliers();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setError(error.response?.data?.error?.message || 'Failed to save supplier');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSupplier) return;
    setIsSaving(true);

    try {
      await adminApi.deleteSupplier(selectedSupplier.id);
      setIsDeleteDialogOpen(false);
      setSelectedSupplier(null);
      fetchSuppliers();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setError(error.response?.data?.error?.message || 'Failed to delete supplier');
      setIsDeleteDialogOpen(false);
    } finally {
      setIsSaving(false);
    }
  };

  const columns = [
    { key: 'name', label: t('suppliers.name') },
    { key: 'code', label: t('suppliers.code') },
    {
      key: 'rating',
      label: t('suppliers.rating'),
      render: (supplier: Supplier) => supplier.rating.toFixed(1),
    },
    {
      key: 'verified',
      label: t('suppliers.verified'),
      render: (supplier: Supplier) => (
        <span className={`px-2 py-1 text-xs rounded-full ${supplier.isVerified ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
          {supplier.isVerified ? 'Verified' : 'Not Verified'}
        </span>
      ),
    },
    {
      key: 'products',
      label: t('suppliers.productCount'),
      render: (supplier: Supplier) => supplier._count.products,
    },
    {
      key: 'actions',
      label: t('common.actions'),
      render: (supplier: Supplier) => (
        <div className="flex gap-2">
          <button onClick={() => openEditModal(supplier)} className="p-1 text-gray-500 hover:text-primary">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => { setSelectedSupplier(supplier); setIsDeleteDialogOpen(true); }}
            className="p-1 text-gray-500 hover:text-red-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('suppliers.title')}</h1>
        <button onClick={openCreateModal} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark text-sm sm:text-base whitespace-nowrap">
          {t('suppliers.addSupplier')}
        </button>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">{error}</div>}

      <DataTable columns={columns} data={suppliers} isLoading={isLoading} keyExtractor={(supplier) => supplier.id} />

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedSupplier ? t('suppliers.editSupplier') : t('suppliers.addSupplier')}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('suppliers.name')}</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('suppliers.code')}</label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('suppliers.rating')}</label>
            <input
              type="number"
              value={formData.rating}
              onChange={(e) => setFormData({ ...formData, rating: Number(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              min="0"
              max="5"
              step="0.1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('suppliers.description')}</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              rows={3}
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isVerified"
              checked={formData.isVerified}
              onChange={(e) => setFormData({ ...formData, isVerified: e.target.checked })}
              className="w-4 h-4 text-primary"
            />
            <label htmlFor="isVerified" className="text-sm font-medium text-gray-700">{t('suppliers.verified')}</label>
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={isSaving} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50">
              {isSaving ? 'Saving...' : t('common.save')}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title={t('suppliers.deleteSupplier')}
        message={t('suppliers.deleteConfirm')}
        isLoading={isSaving}
      />
    </div>
  );
}
